import React, { useEffect, useState, useRef } from 'react'
import { motion, useAnimation, useInView } from 'framer-motion'
import Button from './shared/Button'
import CTAFace from './CTAFace'
import { TbArrowRight, TbPhoneCall } from 'react-icons/tb'
import { Link } from 'gatsby'

export default function CTA() {
  const sectionRef = useRef(null)
  const isInView = useInView(sectionRef, { once: true, margin: "-100px 0px" })
  const controls = useAnimation()

  useEffect(() => {
    if (isInView) {
      controls.start("visible")
    }
  }, [isInView, controls])

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  }

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  }

  // Background decorative shapes
  const backgroundShapes = [
    { top: '10%', left: '5%', size: 120, opacity: 0.05, blur: 60, color: 'primary-300', delay: 0.2 },
    { top: '60%', right: '8%', size: 180, opacity: 0.07, blur: 70, color: 'primary-400', delay: 0.5 },
    { top: '30%', left: '85%', size: 100, opacity: 0.06, blur: 50, color: 'primary-500', delay: 0.8 },
    { top: '80%', left: '20%', size: 150, opacity: 0.04, blur: 60, color: 'primary-200', delay: 1.1 },
  ]

  // Face animation paths
  const faceAnimations = [
    { x: [0, 15, -10, 5, 0], y: [0, -10, 5, -5, 0], duration: 20, delay: 0 },
    { x: [0, -20, 10, -5, 0], y: [0, 5, -15, 10, 0], duration: 25, delay: 2 },
    { x: [0, 10, -15, 5, 0], y: [0, -5, -10, 15, 0], duration: 18, delay: 1 },
    { x: [0, -5, 20, -10, 0], y: [0, 15, 5, -10, 0], duration: 22, delay: 3 },
    { x: [0, 15, -5, 10, 0], y: [0, 10, -15, 5, 0], duration: 19, delay: 2 },
    { x: [0, -10, 5, -15, 0], y: [0, -5, 15, -10, 0], duration: 21, delay: 1 },
    { x: [0, 5, -10, 15, 0], y: [0, -15, 5, -5, 0], duration: 23, delay: 0 },
    { x: [0, -15, 10, -5, 0], y: [0, 5, -5, 10, 0], duration: 24, delay: 2 },
    { x: [0, 10, -5, 15, 0], y: [0, -10, 15, -5, 0], duration: 26, delay: 3 },
    { x: [0, -5, 15, -10, 0], y: [0, 10, -5, 15, 0], duration: 22, delay: 1 },
  ]

  return (
    <section 
      id="cta" 
      ref={sectionRef}
      className='py-24 lg:py-36 px-6 lg:px-24 flex flex-col items-center justify-center gap-4 relative overflow-hidden bg-gradient-to-b from-gray-50 to-white'
    >
      {/* Animated background shapes */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        {backgroundShapes.map((shape, index) => (
          <motion.div
            key={index}
            className={`absolute rounded-full bg-${shape.color}`}
            style={{
              top: shape.top,
              left: shape.left,
              right: shape.right,
              width: shape.size,
              height: shape.size,
              opacity: 0,
              filter: `blur(${shape.blur}px)`
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ 
              opacity: shape.opacity, 
              scale: 1,
              x: [0, 10, -5, 8, 0],
              y: [0, -8, 5, -10, 0]
            }}
            transition={{
              opacity: { delay: shape.delay, duration: 1 },
              scale: { delay: shape.delay, duration: 1.2 },
              x: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: 12 + index * 2,
                ease: "easeInOut"
              },
              y: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: 10 + index * 2,
                ease: "easeInOut"
              }
            }}
          />
        ))}
        
        {/* Decorative grid pattern */}
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.03] pointer-events-none"></div>
      </div>

      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/10 rounded-bl-full"></div>
      <div className="absolute bottom-0 left-0 w-40 h-40 bg-primary-500/10 rounded-tr-full"></div>

      {/* Subtle diagonal lines */}
      <div className="absolute inset-0 opacity-[0.02]" 
        style={{
          backgroundImage: 'linear-gradient(45deg, #000 25%, transparent 25%, transparent 75%, #000 75%, #000), linear-gradient(45deg, #000 25%, transparent 25%, transparent 75%, #000 75%, #000)',
          backgroundSize: '60px 60px',
          backgroundPosition: '0 0, 30px 30px'
        }}
      ></div>

      {/* Glowing orbs */}
      <motion.div 
        className="absolute top-[15%] left-[10%] w-24 h-24 rounded-full bg-primary-300/20"
        style={{ filter: 'blur(40px)' }}
        animate={{ 
          scale: [1, 1.2, 1],
          opacity: [0.2, 0.3, 0.2]
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      <motion.div 
        className="absolute bottom-[20%] right-[15%] w-32 h-32 rounded-full bg-primary-400/15"
        style={{ filter: 'blur(50px)' }}
        animate={{ 
          scale: [1, 1.3, 1],
          opacity: [0.15, 0.25, 0.15]
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2
        }}
      />

      <div className='w-full flex items-stretch justify-between lg:gap-8 relative z-10'>
        <div className='flex-[0.5] relative -mt-36 -mb-36 hidden lg:block'>
          <motion.div
            key="left-0"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: 1,
              x: faceAnimations[0].x,
              y: faceAnimations[0].y,
            }}
            transition={{
              opacity: { duration: 0.5, delay: 0.5 },
              x: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: faceAnimations[0].duration,
                ease: "easeInOut",
                delay: faceAnimations[0].delay
              },
              y: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: faceAnimations[0].duration,
                ease: "easeInOut",
                delay: faceAnimations[0].delay
              }
            }}
            className="absolute left-[10%] top-[10%]"
          >
            <CTAFace sizeClassName="w-[80px] h-[80px]" />
          </motion.div>
          
          <motion.div
            key="left-1"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: 1,
              x: faceAnimations[1].x,
              y: faceAnimations[1].y,
            }}
            transition={{
              opacity: { duration: 0.5, delay: 0.6 },
              x: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: faceAnimations[1].duration,
                ease: "easeInOut",
                delay: faceAnimations[1].delay
              },
              y: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: faceAnimations[1].duration,
                ease: "easeInOut",
                delay: faceAnimations[1].delay
              }
            }}
            className="absolute left-[30%] top-[25%]"
          >
            <CTAFace sizeClassName="w-[95px] h-[95px]" />
          </motion.div>
          
          <motion.div
            key="left-2"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: 1,
              x: faceAnimations[2].x,
              y: faceAnimations[2].y,
            }}
            transition={{
              opacity: { duration: 0.5, delay: 0.7 },
              x: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: faceAnimations[2].duration,
                ease: "easeInOut",
                delay: faceAnimations[2].delay
              },
              y: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: faceAnimations[2].duration,
                ease: "easeInOut",
                delay: faceAnimations[2].delay
              }
            }}
            className="absolute left-[65%] top-[45%]"
          >
            <CTAFace sizeClassName="w-[80px] h-[80px]" />
          </motion.div>
          
          <motion.div
            key="left-3"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: 1,
              x: faceAnimations[3].x,
              y: faceAnimations[3].y,
            }}
            transition={{
              opacity: { duration: 0.5, delay: 0.8 },
              x: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: faceAnimations[3].duration,
                ease: "easeInOut",
                delay: faceAnimations[3].delay
              },
              y: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: faceAnimations[3].duration,
                ease: "easeInOut",
                delay: faceAnimations[3].delay
              }
            }}
            className="absolute left-[10%] top-[65%]"
          >
            <CTAFace sizeClassName="w-[85px] h-[85px]" />
          </motion.div>
          
          <motion.div
            key="left-4"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: 1,
              x: faceAnimations[4].x,
              y: faceAnimations[4].y,
            }}
            transition={{
              opacity: { duration: 0.5, delay: 0.9 },
              x: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: faceAnimations[4].duration,
                ease: "easeInOut",
                delay: faceAnimations[4].delay
              },
              y: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: faceAnimations[4].duration,
                ease: "easeInOut",
                delay: faceAnimations[4].delay
              }
            }}
            className="absolute left-[90%] top-[75%]"
          >
            <CTAFace sizeClassName="w-[60px] h-[60px]" />
          </motion.div>
        </div>

        <motion.div 
          className='flex-1 shrink-0 flex flex-col gap-6 items-center justify-center w-full text-center'
          variants={containerVariants}
          initial="hidden"
          animate={controls}
        >
          <motion.div 
            variants={itemVariants}
            className="inline-block px-4 py-2 rounded-full bg-primary-100 text-primary-600 text-sm"
          >
            <span className='font-comico'>WHY WAIT?</span>
          </motion.div>
          
          <motion.h2 
            variants={itemVariants}
            className='text-5xl lg:text-6xl'
          >
            Ready to <span className='relative text-primary-500'>
              elevate
              <span className="absolute -bottom-2 left-0 w-full h-3 bg-primary-300/30 -z-10 rounded-full"></span>
            </span> your sales game?
          </motion.h2>
          
          <motion.p 
            variants={itemVariants}
            className='max-w-[600px] text-gray-700 mt-2 text-lg'
          >
            Join thousands of businesses that have transformed their sales process with PipeLime's AI-powered automation.
          </motion.p>
          
          <motion.div
            variants={itemVariants}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="mt-4"
          >
            <a 
              href="https://meet.brevo.com/pipelime/drive-more-sales"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button 
                size="lg" 
                className="shadow-lg shadow-primary-500/20 hover:shadow-primary-500/30 transition-all flex items-center gap-2 group"
              >
                <TbPhoneCall className="text-lg" />
                Schedule a call
                <TbArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </a>
          </motion.div>
          
          <motion.div
            variants={itemVariants}
            className="mt-2"
          >
            <Link 
              to="/pricing" 
              className="inline-flex items-center gap-2 text-primary-600 font-medium hover:text-primary-700 transition-all group"
            >
              View pricing plans
              <TbArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </motion.div>

        <div className='flex-[0.5] relative -mt-36 -mb-36 hidden lg:block'>
          <motion.div
            key="right-0"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: 1,
              x: faceAnimations[5].x,
              y: faceAnimations[5].y,
            }}
            transition={{
              opacity: { duration: 0.5, delay: 0.5 },
              x: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: faceAnimations[5].duration,
                ease: "easeInOut",
                delay: faceAnimations[5].delay
              },
              y: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: faceAnimations[5].duration,
                ease: "easeInOut",
                delay: faceAnimations[5].delay
              }
            }}
            className="absolute right-[50%] top-[10%]"
          >
            <CTAFace sizeClassName="w-[80px] h-[80px]" />
          </motion.div>
          
          <motion.div
            key="right-1"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: 1,
              x: faceAnimations[6].x,
              y: faceAnimations[6].y,
            }}
            transition={{
              opacity: { duration: 0.5, delay: 0.6 },
              x: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: faceAnimations[6].duration,
                ease: "easeInOut",
                delay: faceAnimations[6].delay
              },
              y: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: faceAnimations[6].duration,
                ease: "easeInOut",
                delay: faceAnimations[6].delay
              }
            }}
            className="absolute right-[0%] top-[25%]"
          >
            <CTAFace sizeClassName="w-[95px] h-[95px]" />
          </motion.div>
          
          <motion.div
            key="right-2"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: 1,
              x: faceAnimations[7].x,
              y: faceAnimations[7].y,
            }}
            transition={{
              opacity: { duration: 0.5, delay: 0.7 },
              x: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: faceAnimations[7].duration,
                ease: "easeInOut",
                delay: faceAnimations[7].delay
              },
              y: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: faceAnimations[7].duration,
                ease: "easeInOut",
                delay: faceAnimations[7].delay
              }
            }}
            className="absolute right-[65%] top-[45%]"
          >
            <CTAFace sizeClassName="w-[80px] h-[80px]" />
          </motion.div>
          
          <motion.div
            key="right-3"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: 1,
              x: faceAnimations[8].x,
              y: faceAnimations[8].y,
            }}
            transition={{
              opacity: { duration: 0.5, delay: 0.8 },
              x: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: faceAnimations[8].duration,
                ease: "easeInOut",
                delay: faceAnimations[8].delay
              },
              y: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: faceAnimations[8].duration,
                ease: "easeInOut",
                delay: faceAnimations[8].delay
              }
            }}
            className="absolute right-[10%] top-[65%]"
          >
            <CTAFace sizeClassName="w-[85px] h-[85px]" />
          </motion.div>
          
          <motion.div
            key="right-4"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: 1,
              x: faceAnimations[9].x,
              y: faceAnimations[9].y,
            }}
            transition={{
              opacity: { duration: 0.5, delay: 0.9 },
              x: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: faceAnimations[9].duration,
                ease: "easeInOut",
                delay: faceAnimations[9].delay
              },
              y: { 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: faceAnimations[9].duration,
                ease: "easeInOut",
                delay: faceAnimations[9].delay
              }
            }}
            className="absolute right-[90%] top-[75%]"
          >
            <CTAFace sizeClassName="w-[60px] h-[60px]" />
          </motion.div>
        </div>
      </div>

      {/* Mobile faces (visible only on small screens) */}
      <div className="lg:hidden w-full flex flex-wrap justify-center gap-4 mt-8">
        {[0, 1, 2, 3].map((index) => (
          <motion.div
            key={`mobile-${index}`}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ 
              opacity: 1,
              scale: 1,
              y: [0, -5, 0, 5, 0]
            }}
            transition={{
              opacity: { duration: 0.5, delay: 0.2 + (index * 0.1) },
              scale: { duration: 0.5, delay: 0.2 + (index * 0.1) },
              y: { 
                repeat: Infinity, 
                repeatType: "loop", 
                duration: 3 + index,
                ease: "easeInOut",
                delay: index * 0.5
              }
            }}
          >
            <CTAFace sizeClassName="!w-[65px] !h-[65px]" />
          </motion.div>
        ))}
      </div>

      {/* Subtle dot pattern overlay */}
      <div className="absolute inset-0 opacity-[0.02]" 
        style={{
          backgroundImage: 'radial-gradient(#000 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }}
      ></div>
    </section>  
  )
}
